import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, Sparkles } from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { logger } from "@/utils/logger";

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [showRoyaltyForm, setShowRoyaltyForm] = useState(false);
  const [foundingMembersRemaining, setFoundingMembersRemaining] = useState(null);
  const [royaltyData, setRoyaltyData] = useState({ 
    ownerName: "",
    businessName: "",
    contactPhone: "",
    agency: "", 
    registrationNumber: "",
    ein: "", 
    website: "" 
  });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showFreeDisclosure, setShowFreeDisclosure] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/subscription/plans');
      setPlans(response.data.plans);
      setFoundingMembersRemaining(response.data.founding_members_remaining);
    } catch (error) {
      logger.error('Failed to fetch plans:', error);
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (tier) => {
    // Free tier doesn't require verification form
    if (tier === 'free') {
      if (!user) {
        navigate('/signin');
        return;
      }
      setShowFreeDisclosure(true);
      return;
    }
    // All paid tiers require verification
    setSelectedTier(tier);
    setShowRoyaltyForm(true);
  };

  const handleFreeTierSignup = async () => {
    if (!user) {
      navigate('/signin');
      return;
    }
    
    setCheckoutLoading(true);
    try {
      const response = await api.post('/subscription/activate-free');
      if (response.data.already_activated) {
        navigate(response.data.redirect || '/dashboard/breeder');
        return;
      }
      // The $5.99 activation verifies the Breedling customer payment rail and
      // confirms any reserved founding spot. Seller payouts are deferred until
      // the first paid listing is published.
      window.location.assign(response.data.url);
    } catch (error) {
      logger.error('Free tier signup failed:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to activate free tier';
      toast.error(errorMessage);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleRoyaltySubmit = async (e) => {
    e.preventDefault();
    
    // All paid tiers require owner information
    if (!royaltyData.ownerName || !royaltyData.contactPhone) {
      toast.error('Please provide your full name and contact phone');
      return;
    }
    
    // All paid tiers require agency and registration number
    if (!royaltyData.agency || !royaltyData.registrationNumber) {
      toast.error('Please provide your registered agency and registration number');
      return;
    }
    
    // Professional and Royalty tiers require EIN
    if ((selectedTier === 'elite' || selectedTier === 'royalty') && !royaltyData.ein) {
      toast.error('Please provide your EIN');
      return;
    }
    
    // Royalty tier requires website
    if (selectedTier === 'royalty' && !royaltyData.website) {
      toast.error('Please provide your business website');
      return;
    }
    
    await handleCheckout(selectedTier, royaltyData);
  };

  const handleCheckout = async (tier, verificationData = null) => {
    if (!user) {
      navigate('/signin');
      return;
    }

    setCheckoutLoading(true);
    
    try {
      const params = new URLSearchParams({ tier });
      
      if (verificationData) {
        params.append('owner_name', verificationData.ownerName);
        params.append('business_name', verificationData.businessName || '');
        params.append('contact_phone', verificationData.contactPhone);
        params.append('agency', verificationData.agency);
        params.append('registration_number', verificationData.registrationNumber);
        
        if (verificationData.ein) {
          params.append('ein', verificationData.ein);
        }
        
        if (verificationData.website) {
          params.append('website', verificationData.website);
        }
      }
      
      const response = await api.post(`/subscription/create-checkout?${params.toString()}`);
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      logger.error('Checkout failed:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create checkout session';
      toast.error(errorMessage);
      setCheckoutLoading(false);
    }
  };

  if (showFreeDisclosure) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 pb-16 px-6">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => setShowFreeDisclosure(false)}
            className="text-[#A1A1AA] hover:text-[#D4AF37] mb-6 flex items-center gap-2"
          >
            ← Back to plans
          </button>

          <div className="glass-card p-8 border-[#22C55E]/30">
            <h2 className="text-2xl text-[#EDEDED] mb-2">Review Breedling Free</h2>
            <p className="text-[#A1A1AA] mb-6">
              Review the charges below before continuing to Stripe.
            </p>

            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between p-4 rounded-lg bg-[#18181B] border border-[#27272A]">
                <div>
                  <p className="text-[#EDEDED]">Breedling Free</p>
                  <p className="text-xs text-[#A1A1AA]">Breeder plan subscription</p>
                </div>
                <strong className="text-[#22C55E]">$0/month</strong>
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-[#18181B] border border-[#27272A]">
                <div>
                  <p className="text-[#EDEDED]">Account activation</p>
                  <p className="text-xs text-[#A1A1AA]">Charged today</p>
                </div>
                <strong className="text-[#EDEDED]">$5.99 once</strong>
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-[#18181B] border border-[#27272A]">
                <div>
                  <p className="text-[#EDEDED]">Annual registration</p>
                  <p className="text-xs text-[#A1A1AA]">$59.99/year from your signup anniversary after any founding-member waiver</p>
                </div>
                <strong className="text-[#EDEDED]">$59.99/year</strong>
              </div>
            </div>

            {foundingMembersRemaining > 0 && (
              <div className="mb-6 p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg">
                <p className="text-sm text-[#D4AF37]">
                  Your paid activation reserves the next available founding-member spot. Depending on your member number, your annual registration fee will be waived for 1–3 years.
                </p>
              </div>
            )}

            <p className="text-xs text-[#A1A1AA] mb-5">
              Stripe will charge $5.99 today and save your payment method for future authorized Breedling charges. The annual fee is not charged today.
            </p>

            <button
              onClick={handleFreeTierSignup}
              className="btn-gold w-full"
              disabled={checkoutLoading}
            >
              {checkoutLoading ? 'Opening Stripe...' : 'Pay $5.99 & Activate Free Plan'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (showRoyaltyForm) {
    const isRoyalty = selectedTier === 'royalty';
    const isProfessional = selectedTier === 'elite';
    const isBasicOrStarter = selectedTier === 'basic' || selectedTier === 'pro';
    
    const tierNames = {
      'basic': 'Breedling Basic',
      'pro': 'Breedling Starter',
      'elite': 'Breedling Professional',
      'royalty': 'Breedling Royalty'
    };
    
    const tierPrices = {
      'basic': '$10.99/month',
      'pro': '$19.99/month',
      'elite': '$29.99/month',
      'royalty': '$99.99/month'
    };
    
    return (
      <div className="min-h-screen bg-[#050505] pt-24 pb-16 px-6">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => setShowRoyaltyForm(false)}
            className="text-[#A1A1AA] hover:text-[#D4AF37] mb-6 flex items-center gap-2"
          >
            ← Back to plans
          </button>
          
          <div className="glass-card p-8">
            <div className="flex items-center gap-3 mb-6">
              {isRoyalty ? (
                <Crown className="w-8 h-8 text-[#D4AF37]" />
              ) : isProfessional ? (
                <Sparkles className="w-8 h-8 text-[#A1A1AA]" />
              ) : (
                <Check className="w-8 h-8 text-[#D4AF37]" />
              )}
              <div>
                <h2 className="text-2xl text-[#EDEDED]">{tierNames[selectedTier]}</h2>
                <p className="text-sm text-[#A1A1AA]">Breeder verification required</p>
              </div>
            </div>

            <p className="text-[#A1A1AA] mb-6">
              All Breedling breeders must provide owner/agent information and be registered with an official agency. Please complete all required fields below.
            </p>

            <form onSubmit={handleRoyaltySubmit} className="space-y-6">
              {/* Owner/Agent Information Section */}
              <div className="pb-6 border-b border-[#27272A]">
                <h3 className="text-lg text-[#EDEDED] mb-4">Owner / Authorized Agent Information</h3>
                
                {/* Full Name - Required for ALL tiers */}
                <div className="mb-4">
                  <label className="block text-sm text-[#A1A1AA] mb-2">
                    Full Name <span className="text-[#EF4444]">*</span>
                  </label>
                  <input
                    type="text"
                    value={royaltyData.ownerName}
                    onChange={(e) => setRoyaltyData({ ...royaltyData, ownerName: e.target.value })}
                    className="input-minimal"
                    placeholder="Your legal name"
                    required
                  />
                </div>

                {/* Business/Kennel Name - Optional for ALL tiers */}
                <div className="mb-4">
                  <label className="block text-sm text-[#A1A1AA] mb-2">
                    Business / Kennel Name <span className="text-[#71717A]">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={royaltyData.businessName}
                    onChange={(e) => setRoyaltyData({ ...royaltyData, businessName: e.target.value })}
                    className="input-minimal"
                    placeholder="Your breeding program or kennel name"
                  />
                </div>

                {/* Contact Phone - Required for ALL tiers */}
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">
                    Contact Phone <span className="text-[#EF4444]">*</span>
                  </label>
                  <input
                    type="tel"
                    value={royaltyData.contactPhone}
                    onChange={(e) => setRoyaltyData({ ...royaltyData, contactPhone: e.target.value })}
                    className="input-minimal"
                    placeholder="(555) 123-4567"
                    required
                  />
                </div>
              </div>

              {/* Animal Registry Information Section */}
              <div className="pb-6 border-b border-[#27272A]">
                <h3 className="text-lg text-[#EDEDED] mb-4">Animal Registry Information</h3>
                
                {/* Registry Agency - Required for ALL tiers */}
                <div className="mb-4">
                  <label className="block text-sm text-[#A1A1AA] mb-2">
                    Registered Agency <span className="text-[#EF4444]">*</span>
                  </label>
                  <input
                    type="text"
                    value={royaltyData.agency}
                    onChange={(e) => setRoyaltyData({ ...royaltyData, agency: e.target.value })}
                    className="input-minimal"
                    placeholder="e.g., AKC, TICA, AQHA, ABRA"
                    required
                  />
                  <p className="text-xs text-[#71717A] mt-1">Enter the name of your official registry</p>
                </div>

                {/* Registration Number - Required for ALL tiers */}
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">
                    Registration Number <span className="text-[#EF4444]">*</span>
                  </label>
                  <input
                    type="text"
                    value={royaltyData.registrationNumber}
                    onChange={(e) => setRoyaltyData({ ...royaltyData, registrationNumber: e.target.value })}
                    className="input-minimal"
                    placeholder="Your breeder registration number"
                    required
                  />
                </div>
              </div>

              {/* Business Verification Section - Professional & Royalty only */}
              {(isProfessional || isRoyalty) && (
                <div className="pb-6 border-b border-[#27272A]">
                  <h3 className="text-lg text-[#EDEDED] mb-4">Business Verification</h3>
                  
                  {/* EIN - Required for Professional and Royalty */}
                  <div className={isRoyalty ? "mb-4" : ""}>
                    <label className="block text-sm text-[#A1A1AA] mb-2">
                      Business EIN / Tax ID <span className="text-[#EF4444]">*</span>
                    </label>
                    <input
                      type="text"
                      value={royaltyData.ein}
                      onChange={(e) => setRoyaltyData({ ...royaltyData, ein: e.target.value })}
                      className="input-minimal"
                      placeholder="XX-XXXXXXX"
                      required
                    />
                    <p className="text-xs text-[#71717A] mt-1">Format: XX-XXXXXXX</p>
                  </div>

                  {/* Website - Required only for Royalty */}
                  {isRoyalty && (
                    <div>
                      <label className="block text-sm text-[#A1A1AA] mb-2">
                        Business Website <span className="text-[#EF4444]">*</span>
                      </label>
                      <input
                        type="url"
                        value={royaltyData.website}
                        onChange={(e) => setRoyaltyData({ ...royaltyData, website: e.target.value })}
                        className="input-minimal"
                        placeholder="https://yourbreeder.com"
                        required={isRoyalty}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg p-4">
                <p className="text-sm text-[#D4AF37]">
                  <strong>Note:</strong> Your registration will be verified. {isRoyalty || isProfessional ? 'Additional verification for professional tiers may take 1-2 business days.' : 'Basic verification is typically completed within 24 hours.'}
                </p>
              </div>

              <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4 space-y-2">
                <p className="text-sm text-[#EDEDED]"><strong>Billing disclosure</strong></p>
                <p className="text-sm text-[#A1A1AA]">Plan: {tierPrices[selectedTier]}</p>
                {foundingMembersRemaining > 0 ? (
                  <>
                    <p className="text-sm text-[#A1A1AA]">Founding-member activation: $5.99 today</p>
                    <p className="text-sm text-[#A1A1AA]">Monthly plan billing begins after your first sale.</p>
                    <p className="text-sm text-[#A1A1AA]">Annual registration: $59.99/year from your signup anniversary after your 1–3 year founding waiver.</p>
                  </>
                ) : (
                  <p className="text-sm text-[#A1A1AA]">The monthly plan and $59.99 annual registration are charged at checkout.</p>
                )}
              </div>

              <button
                type="submit"
                className="btn-gold w-full"
                disabled={checkoutLoading}
              >
                {checkoutLoading ? 'Processing...' : `Continue to Payment (${tierPrices[selectedTier]})`}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Subscription Plans</p>
          <h1 className="text-4xl sm:text-5xl text-[#EDEDED] mb-4">Choose Your Plan</h1>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto">
            Select the plan that best fits your breeding business.
          </p>
        </div>

        {/* Founding Members Promotion Banner */}
        {foundingMembersRemaining !== null && foundingMembersRemaining > 0 && (
          <div className="mb-8 mx-auto max-w-4xl">
            <div className="glass-card p-6 border-[#D4AF37] bg-gradient-to-r from-[#D4AF37]/10 to-transparent">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <svg className="w-12 h-12 text-[#D4AF37]" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl text-[#D4AF37] font-semibold mb-2">Founding Members - Limited Time!</h3>
                  <p className="text-[#EDEDED] mb-3">
                    Join the <strong>first 50 breeders</strong> and have your <strong className="text-[#D4AF37]">$59.99/year annual registration fee</strong> waived:
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <div className="bg-[#18181B] p-3 rounded-lg border border-[#27272A]">
                      <div className="text-[#D4AF37] font-bold mb-1">First 10</div>
                      <div className="text-sm text-[#A1A1AA]">
                        3 years annual fee waived
                        <div className="text-xs text-[#71717A] mt-1">Save $179.97</div>
                      </div>
                    </div>
                    <div className="bg-[#18181B] p-3 rounded-lg border border-[#27272A]">
                      <div className="text-[#D4AF37] font-bold mb-1">Members 11-30</div>
                      <div className="text-sm text-[#A1A1AA]">
                        2 years annual fee waived
                        <div className="text-xs text-[#71717A] mt-1">Save $119.98</div>
                      </div>
                    </div>
                    <div className="bg-[#18181B] p-3 rounded-lg border border-[#27272A]">
                      <div className="text-[#D4AF37] font-bold mb-1">Members 31-50</div>
                      <div className="text-sm text-[#A1A1AA]">
                        1 year annual fee waived
                        <div className="text-xs text-[#71717A] mt-1">Save $59.99</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-3">
                    <div className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg font-bold">
                      {foundingMembersRemaining} Spots Left
                    </div>
                    <div className="text-sm text-[#A1A1AA]">
                      Out of 50 founding member spots
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <p className="text-[#EDEDED]">
                      ✅ <strong className="text-[#D4AF37]">Pay only $5.99 activation fee</strong> to verify payment rails
                    </p>
                    <p className="text-[#EDEDED]">
                      ✅ <strong className="text-[#D4AF37]">Subscription paused until your first sale</strong> - start earning before paying!
                    </p>
                    <p className="text-[#EDEDED]">
                      ✅ <strong className="text-[#D4AF37]">Your Breedlingversary:</strong> Annual fees calculated from your signup date
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {plans.map((plan) => {
            const isRoyalty = plan.tier === 'royalty';
            const isElite = plan.tier === 'elite';
            const isFree = plan.tier === 'free';
            
            return (
              <div
                key={plan.tier}
                className={`glass-card p-6 relative ${
                  isRoyalty ? 'border-[#D4AF37] shadow-lg shadow-[#D4AF37]/20' : ''
                } ${isElite ? 'border-[#A1A1AA]' : ''} ${isFree ? 'border-[#22C55E]/30' : ''}`}
              >
                {isRoyalty && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black text-xs px-3 py-1 rounded-full font-medium">
                    EXCLUSIVE
                  </div>
                )}
                {isFree && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#22C55E] text-black text-xs px-3 py-1 rounded-full font-medium">
                    START FREE
                  </div>
                )}
                
                <div className="mb-6">
                  {isRoyalty && <Crown className="w-8 h-8 text-[#D4AF37] mb-2" />}
                  {isElite && <Sparkles className="w-8 h-8 text-[#A1A1AA] mb-2" />}
                  
                  <h3 className="text-xl text-[#EDEDED] mb-2">{plan.name}</h3>
                  <div className="flex items-baseline gap-1">
                    {isFree ? (
                      <span className="text-3xl text-[#22C55E] font-bold">Free</span>
                    ) : (
                      <>
                        <span className="text-3xl text-[#EDEDED] font-bold">${plan.price}</span>
                        <span className="text-[#71717A]">/{plan.interval}</span>
                      </>
                    )}
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={`${plan.tier}-feature-${index}`} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 ${isFree ? 'text-[#22C55E]' : 'text-[#D4AF37]'} mt-0.5 flex-shrink-0`} />
                      <span className="text-[#A1A1AA]">{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan.requires_verification && (
                  <div className="mb-4 p-3 bg-[#D4AF37]/10 rounded-lg border border-[#D4AF37]/20">
                    <p className="text-xs text-[#D4AF37]">
                      <strong>Requires:</strong> {plan.verification_requirements.join(', ')}
                    </p>
                  </div>
                )}
                
                {isFree && (
                  <div className="mb-4 p-3 bg-[#22C55E]/10 rounded-lg border border-[#22C55E]/20">
                    <p className="text-xs text-[#22C55E] mb-1">
                      No monthly subscription fee
                    </p>
                    <p className="text-xs text-[#A1A1AA]">
                      $5.99 one-time account activation
                    </p>
                  </div>
                )}

                <div className="mb-4 p-3 bg-[#18181B] rounded-lg border border-[#27272A] space-y-1">
                  {!isFree && foundingMembersRemaining > 0 && (
                    <p className="text-xs text-[#A1A1AA]">$5.99 founding-member activation today; monthly billing begins after your first sale.</p>
                  )}
                  <p className="text-xs text-[#EDEDED] font-medium">$59.99 annual registration fee</p>
                  <p className="text-[11px] text-[#71717A]">Due yearly from your signup anniversary after any founding-member waiver.</p>
                </div>

                <button
                  onClick={() => handleSelectPlan(plan.tier)}
                  className={`w-full py-2.5 px-4 rounded-lg font-medium transition-all ${
                    isFree
                      ? 'bg-[#22C55E] text-black hover:bg-[#16A34A]'
                      : 'bg-[#D4AF37] text-black hover:bg-[#C4A030] shadow-sm shadow-[#D4AF37]/20'
                  }`}
                  disabled={checkoutLoading}
                >
                  {checkoutLoading ? 'Loading...' : isFree ? 'Get Started Free' : 'Select Plan'}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center text-sm text-[#71717A] space-y-2">
          <p>All subscriptions can be cancelled at any time. No long-term commitments.</p>
          <p>A <strong className="text-[#A1A1AA]">$59.99/year annual registration fee</strong> applies to every breeder plan, including Breedling Free, after any founding-member waiver.</p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
